// src/components/ProductIncomingInputPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import "./ProductIncoming.css";

const API_URL = process.env.REACT_APP_API_BASE_URL || "https://sipedasapi.bkkpangkalpinang.com/api";

export default function ProductIncomingInputPage() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    product_id: "",
    source: "pembelian",
    source_note: "",
    pabrik: "",
    tanggal_masuk: "",
    batch: "",
    stok_masuk: "",
    tanggal_kadaluarsa: "",
    keterangan: "",
  });

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const res = await axios.get(`${API_URL}/products`);
      const list = res?.data?.data ?? res?.data ?? [];
      setProducts(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const selectedProduct = useMemo(() => {
    return products.find((p) => String(p.id) === String(form.product_id));
  }, [products, form.product_id]);

  const productLabel = (p) => p?.name ?? p?.nama_produk ?? p?.nama ?? `Produk #${p?.id}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.product_id) return alert("Pilih produk terlebih dahulu.");

    if (form.source === "transfer_masuk" && !form.source_note.trim()) {
      return alert("Keterangan sumber wajib jika Transfer Masuk.");
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        product_id: Number(form.product_id),
        stok_masuk: Number(form.stok_masuk),
        source_note: form.source === "transfer_masuk" ? form.source_note : null,
      };

      await axios.post(`${API_URL}/incoming-products`, payload);

      alert("Produk masuk berhasil disimpan.");
      navigate("/admin/produk-masuk");
    } catch (e2) {
      console.error(e2);
      const msg =
        e2?.response?.data?.message ||
        (e2?.response?.data?.errors ? JSON.stringify(e2.response.data.errors) : null) ||
        "Gagal menyimpan produk masuk.";
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pi-page">
      <div className="pi-head">
        <div>
          <h2 className="pi-title">Input Produk Masuk</h2>
          <div className="pi-subtitle">Isi data barang masuk dengan lengkap</div>
        </div>

        <div className="pi-actions">
          <button type="button" className="pi-btn pi-btn-light" onClick={() => navigate("/admin/produk-masuk")}>
            <FaArrowLeft /> Kembali
          </button>
        </div>
      </div>

      <div className="pi-card">
        <form onSubmit={onSubmit} className="pi-form">
          {/* Produk */}
          <div className="pi-section">
            <div className="pi-sectionTitle">Informasi Produk</div>

            <div className="pi-grid">
              <div className="pi-field">
                <label>Nama Produk</label>
                <select
                  className="pi-select"
                  value={form.product_id}
                  disabled={loadingProducts}
                  onChange={(e) => setField("product_id", e.target.value)}
                  required
                >
                  <option value="">{loadingProducts ? "Memuat produk..." : "-- Pilih Produk --"}</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {productLabel(p)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pi-field">
                <label>Id Produk</label>
                <input
                  className="pi-input"
                  value={selectedProduct?.id ?? ""}
                  readOnly
                  placeholder="Otomatis setelah pilih produk"
                />
              </div>
            </div>
          </div>

          {/* Sumber */}
          <div className="pi-section">
            <div className="pi-sectionTitle">Sumber Produk</div>

            <div className="pi-grid">
              <div className="pi-field">
                <label>Sumber</label>
                <select
                  className="pi-select"
                  value={form.source}
                  onChange={(e) => setField("source", e.target.value)}
                  required
                >
                  <option value="pembelian">Pembelian</option>
                  <option value="transfer_masuk">Transfer Masuk</option>
                </select>
              </div>

              {form.source === "transfer_masuk" && (
                <div className="pi-field">
                  <label>Keterangan Sumber</label>
                  <input
                    className="pi-input"
                    value={form.source_note}
                    onChange={(e) => setField("source_note", e.target.value)}
                    placeholder="Contoh: dari gudang A / unit lain"
                    required
                  />
                </div>
              )}
            </div>
          </div>

          {/* Detail */}
          <div className="pi-section">
            <div className="pi-sectionTitle">Detail Masuk</div>

            <div className="pi-grid">
              <div className="pi-field">
                <label>Pabrik</label>
                <input
                  className="pi-input"
                  value={form.pabrik}
                  onChange={(e) => setField("pabrik", e.target.value)}
                  placeholder="Input manual"
                />
              </div>

              <div className="pi-field">
                <label>Tanggal Masuk</label>
                <input
                  type="date"
                  className="pi-input"
                  value={form.tanggal_masuk}
                  onChange={(e) => setField("tanggal_masuk", e.target.value)}
                  required
                />
              </div>

              <div className="pi-field">
                <label>Batch</label>
                <input
                  className="pi-input"
                  value={form.batch}
                  onChange={(e) => setField("batch", e.target.value)}
                  placeholder="Input manual"
                />
              </div>

              <div className="pi-field">
                <label>Stok Masuk</label>
                <input
                  type="number"
                  min={1}
                  className="pi-input"
                  value={form.stok_masuk}
                  onChange={(e) => setField("stok_masuk", e.target.value)}
                  placeholder="Input manual"
                  required
                />
                <div className="pi-help">
                  Stok produk akan otomatis bertambah (backend lock & update).
                </div>
              </div>

              <div className="pi-field">
                <label>Tanggal Kadaluarsa</label>
                <input
                  type="date"
                  className="pi-input"
                  value={form.tanggal_kadaluarsa}
                  onChange={(e) => setField("tanggal_kadaluarsa", e.target.value)}
                />
              </div>

              <div className="pi-field pi-fieldFull">
                <label>Keterangan</label>
                <textarea
                  className="pi-textarea"
                  rows={4}
                  value={form.keterangan}
                  onChange={(e) => setField("keterangan", e.target.value)}
                  placeholder="Input manual"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="pi-formActions">
            <button
              type="button"
              className="pi-btn pi-btn-light"
              onClick={() => navigate("/admin/produk-masuk")}
              disabled={saving}
            >
              Batal
            </button>

            <button type="submit" className="pi-btn pi-btn-primary" disabled={saving}>
              <FaSave /> {saving ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
